import { ChangeEvent, useMemo, useState } from "react"
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap"
import { ImportedTravelOffer, parseTravelOfferTsv } from "./travel-offer-tsv"

const WP_SITE = "https://pristjek.ekstrabladet.dk"
const SUPPORTED_ARTICLE_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp"])

type TravelDoFetch = (path: string, body?: unknown, method?: string) => Promise<Response>

type TravelHeadlineResponse = {
	articleTitle?: string
	discoverTitle?: string
	source?: "ai" | "fallback"
	error?: string
}

type ImageDimensions = {
	width: number
	height: number
}

export type TravelCreatedArticle = {
	articleId: number
	previewLink: string
	editLink: string
	title: string
	topic: string
	articleTypeName: string
}

type TravelArticleDraftResponse = {
	articleId?: number
	previewLink?: string
	editLink?: string
	articleTypeName?: string
	topic?: string
	draft?: {
		title?: string
		topic?: string
	}
	createdPost?: {
		id?: number
	}
	error?: string
}

const PRICE_FIELDS = [
	"flypris_2_voksne_dkk",
	"hotelpris_min_dkk",
	"samlet_pris_dkk",
	"pris_pr_person_dkk",
]

const LINK_FIELDS = [
	"momondo_link",
	"trivago_link_dansk_dateret",
]

const HIDDEN_SECONDARY_FIELDS = new Set([
	"hotelpris_max_dkk",
	"hotelpris_anvendt_dkk",
	"flypriskilde",
	"bookinglink",
])

const PRIMARY_DETAIL_FIELDS = [
	"tilbudstype",
	"destination",
	"land",
	"region",
	"ferietype",
	"afrejsedato",
	"hjemrejsedato",
	"antal_naetter",
	"afrejselufthavn",
	"destinationslufthavn",
	"flyselskab",
	"flyrute",
	"antal_stop",
	"rejsetid",
	"bagage_status",
	"hotelnavn",
	"hotelomraade",
	"hoteladresse",
	"hotelstjerner",
	"hotelrating",
	"hotelrating_antal_anmeldelser",
	"vaerelsestype",
	"bookingudbyder",
	"ledighedsstatus",
	"kontrolniveau",
	"artikeloverskrift_forslag",
]

const FIELD_LABELS: Record<string, string> = {
	tilbudstype: "Tilbudstype",
	destination: "Destination",
	land: "Land",
	region: "Region",
	ferietype: "Ferietype",
	hvorfor_valgt: "Hvorfor tilbuddet er valgt",
	afrejsedato: "Afrejsedato",
	hjemrejsedato: "Hjemrejsedato",
	antal_naetter: "Antal nætter",
	afrejselufthavn: "Afrejselufthavn",
	destinationslufthavn: "Destinationslufthavn",
	flyselskab: "Flyselskab",
	flyrute: "Flyrute",
	antal_stop: "Antal stop",
	rejsetid: "Rejsetid",
	flypris_2_voksne_dkk: "Flypris for 2 voksne",
	flypriskilde: "Flypriskilde",
	bagage_status: "Bagagestatus",
	momondo_link: "Momondo-link",
	hotelnavn: "Hotelnavn",
	hotelomraade: "Hotelområde",
	hoteladresse: "Hoteladresse",
	hotelstjerner: "Hotelstjerner",
	hotelstjerner_kilde: "Kilde til hotelstjerner",
	hotelrating: "Hotelrating",
	hotelrating_kilde: "Kilde til hotelrating",
	hotelrating_antal_anmeldelser: "Antal hotelanmeldelser",
	hoteltype: "Hoteltype",
	hotelkaede: "Hotelkæde",
	checkin_checkout: "Check-in / check-out",
	morgenmad_status: "Morgenmad",
	betalings_og_afbestillingsvilkaar: "Betalings- og afbestillingsvilkår",
	vaerelsestype: "Værelsestype",
	hotelbeskrivelse: "Hotelbeskrivelse",
	hotelfaciliteter: "Hotelfaciliteter",
	hotelpris_min_dkk: "Laveste hotelpris",
	hotelpris_max_dkk: "Højeste hotelpris",
	hotelpris_anvendt_dkk: "Anvendt hotelpris",
	hotelpristype: "Hotelpristype",
	estimeringsmetode: "Estimeringsmetode",
	bookingudbyder: "Bookingudbyder",
	bookinglink: "Bookinglink",
	trivago_link_dansk_dateret: "Dateret Trivago-link",
	trivago_hotel_id: "Trivago hotel-ID",
	trivago_slug: "Trivago-slug",
	samlet_pris_dkk: "Samlet pris",
	pris_pr_person_dkk: "Pris pr. person",
	kontrolniveau: "Kontrolniveau",
	ledighedsstatus: "Ledighedsstatus",
	statusforklaring: "Statusforklaring",
	kontroltidspunkt: "Kontroltidspunkt",
	artikeloverskrift_forslag: "Forslag til artikeloverskrift",
	artikelintro: "Artikelintro",
	artikelvinkel: "Artikelvinkel",
	praktiske_forbehold: "Praktiske forbehold",
}

const priorityFieldNames = new Set([...PRICE_FIELDS, ...LINK_FIELDS, ...PRIMARY_DETAIL_FIELDS])

function getFieldLabel(field: string) {
	if (FIELD_LABELS[field]) return FIELD_LABELS[field]
	const label = field.replace(/_/g, " ")
	return label ? `${label.charAt(0).toUpperCase()}${label.slice(1)}` : field
}

function parsePrice(value?: string) {
	const normalized = String(value || "")
		.trim()
		.replace(/\s/g, "")
		.replace(/\.(?=\d{3}(?:\D|$))/g, "")
		.replace(",", ".")
	const price = Number(normalized)
	return Number.isFinite(price) ? price : null
}

function formatPrice(value?: string) {
	const price = parsePrice(value)
	if (price === null) return value || "-"
	return new Intl.NumberFormat("da-DK", {
		style: "currency",
		currency: "DKK",
		maximumFractionDigits: 0,
	}).format(price)
}

function formatTravelDate(value?: string) {
	if (!value) return "-"
	const parsed = new Date(`${value}T12:00:00`)
	if (Number.isNaN(parsed.getTime())) return value
	return parsed.toLocaleDateString("da-DK", {
		day: "numeric",
		month: "short",
		year: "numeric",
	})
}

function extractHttpUrl(value?: string) {
	const match = String(value || "").match(/https?:\/\/[^\s—]+/i)
	if (!match) return ""
	try {
		const parsed = new URL(match[0])
		return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.toString() : ""
	} catch {
		return ""
	}
}

function readFileAsText(file: File) {
	return new Promise<string>((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => resolve(String(reader.result || ""))
		reader.onerror = () => reject(new Error("Filen kunne ikke læses."))
		reader.readAsText(file, "UTF-8")
	})
}

function readFileAsDataUrl(file: File) {
	return new Promise<string>((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => resolve(String(reader.result || ""))
		reader.onerror = () => reject(new Error("Billedfilen kunne ikke læses."))
		reader.readAsDataURL(file)
	})
}

function headlineContainsPrice(headline: string, value?: string) {
	const price = parsePrice(value)
	if (price === null) return false
	return extractHeadlinePrices(headline).some((headlinePrice) => Math.abs(headlinePrice - price) < 0.005)
}

function headlineMentionsPrice(headline: string) {
	return /\b(?:kr\.?|dkk)\b/i.test(String(headline || ""))
}

function extractHeadlinePrices(headline: string) {
	const prices: number[] = []
	const patterns = [
		/(\d[\d.\s]*(?:,\d{1,2})?)\s*(?:kr\.?|dkk)\b/gi,
		/\b(?:kr\.?|dkk)\s*(\d[\d.\s]*(?:,\d{1,2})?)/gi,
	]
	const text = String(headline || "")
	for (const pattern of patterns) {
		let match = pattern.exec(text)
		while (match) {
			const price = parsePrice(match[1])
			if (price !== null) prices.push(price)
			match = pattern.exec(text)
		}
	}
	return prices
}

function headlineUsesOnlyVerifiedPrices(headline: string, value?: string) {
	const price = parsePrice(value)
	const headlinePrices = extractHeadlinePrices(headline)
	return price !== null
		&& headlinePrices.length > 0
		&& headlinePrices.every((headlinePrice) => Math.abs(headlinePrice - price) < 0.005)
}

function normalizeHeadlineComparison(value?: string) {
	return String(value || "")
		.toLocaleLowerCase("da-DK")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/\s+/g, " ")
		.trim()
}

function headlineIncludesValue(headline: string, value?: string) {
	const normalizedValue = normalizeHeadlineComparison(value)
	return Boolean(normalizedValue) && normalizeHeadlineComparison(headline).includes(normalizedValue)
}

function parseTravelDate(value?: string) {
	const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/)
	if (!match) return null
	const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12))
	if (Number.isNaN(date.getTime())) return null
	return date
}

function getCopenhagenToday(referenceDate = new Date()) {
	const parts = new Intl.DateTimeFormat("en-CA", {
		timeZone: "Europe/Copenhagen",
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	}).formatToParts(referenceDate)
	const getPart = (type: string) => Number(parts.find((part) => part.type === type)?.value || 0)
	return new Date(Date.UTC(getPart("year"), getPart("month") - 1, getPart("day"), 12))
}

function getHeadlineTimingContext(data: Record<string, string>, referenceDate = new Date()) {
	const departure = parseTravelDate(data.afrejsedato)
	const returnDate = parseTravelDate(data.hjemrejsedato)
	if (!departure || !returnDate) {
		return {
			daysUntilDeparture: null,
			departureMonth: "",
			isLastMinute: false,
			isWeekendStay: false,
			isNextWeekend: false,
		}
	}
	const daysUntilDeparture = Math.round((departure.getTime() - getCopenhagenToday(referenceDate).getTime()) / (24 * 60 * 60 * 1000))
	const departureDay = departure.getUTCDay()
	const returnDay = returnDate.getUTCDay()
	const nights = Number(data.antal_naetter || 0)
	const isWeekendStay = nights >= 1
		&& nights <= 4
		&& [4, 5, 6].includes(departureDay)
		&& [0, 1].includes(returnDay)
	return {
		daysUntilDeparture,
		departureMonth: new Intl.DateTimeFormat("da-DK", {
			month: "long",
			timeZone: "UTC",
		}).format(departure),
		isLastMinute: daysUntilDeparture >= 0 && daysUntilDeparture <= 30,
		isWeekendStay,
		isNextWeekend: isWeekendStay && daysUntilDeparture >= 1 && daysUntilDeparture <= 10,
	}
}

function feedTitleHasConcreteHook(headline: string, data: Record<string, string>, referenceDate = new Date()) {
	const timing = getHeadlineTimingContext(data, referenceDate)
	if (headlineMentionsPrice(headline) && headlineContainsPrice(headline, data.pris_pr_person_dkk)) return true
	const nights = Number(data.antal_naetter || 0)
	if (nights > 0 && new RegExp(`\\b${nights}\\s*(?:nætter|dage)\\b`, "i").test(headline)) return true
	if (timing.departureMonth && headlineIncludesValue(headline, timing.departureMonth)) return true
	if (Number.isInteger(timing.daysUntilDeparture)
		&& new RegExp(`\\b${timing.daysUntilDeparture}\\s*dage\\b`, "i").test(headline)) return true
	if (timing.isLastMinute && /\blast[\s-]?minute\b/i.test(headline)) return true
	if (timing.isWeekendStay && /\bweekend(?:ophold|tur)?\b/i.test(headline)) return true
	if (data.antal_stop === "0" && /\bdirekte(?:\s+fly)?\b/i.test(headline)) return true
	if (data.hotelstjerner && new RegExp(`\\b${data.hotelstjerner}[- ]?stjernet\\b`, "i").test(headline)) return true
	return [
		data.hotelnavn,
		data.hotelomraade,
		data.flyselskab,
	].filter((value) => normalizeHeadlineComparison(value).length >= 4)
		.some((value) => headlineIncludesValue(headline, value))
}

export function getTravelHeadlineSetupIssues({
	articleTitle,
	discoverTitle,
	data,
	referenceDate = new Date(),
}: {
	articleTitle: string
	discoverTitle: string
	data: Record<string, string>
	referenceDate?: Date
}) {
	const issues: string[] = []
	const destination = data.destination || ""
	const timing = getHeadlineTimingContext(data, referenceDate)
	if (!articleTitle.trim()) issues.push("Artikeloverskriften mangler.")
	if (articleTitle.trim().length > 110) issues.push("Artikeloverskriften må højst være 110 tegn.")
	if (destination && !headlineIncludesValue(articleTitle, destination)) issues.push("Artikeloverskriften skal indeholde destinationen.")
	if (!headlineContainsPrice(articleTitle, data.pris_pr_person_dkk)) issues.push("Artikeloverskriften skal indeholde den verificerede pris.")
	if (!discoverTitle.trim()) issues.push("Discover-/SEO-titlen mangler.")
	if (discoverTitle.trim().length > 80) issues.push("Discover-/SEO-titlen må højst være 80 tegn.")
	if (destination && !headlineIncludesValue(discoverTitle, destination)) issues.push("Discover-/SEO-titlen skal indeholde destinationen.")
	if (headlineMentionsPrice(discoverTitle) && !headlineUsesOnlyVerifiedPrices(discoverTitle, data.pris_pr_person_dkk)) {
		issues.push("Discover-/SEO-titlen indeholder en anden pris end den verificerede pris.")
	}
	if (discoverTitle.trim() && !feedTitleHasConcreteHook(discoverTitle, data, referenceDate)) {
		issues.push("Discover-/SEO-titlen skal indeholde en konkret tilbudsdetalje.")
	}
	if (!timing.isLastMinute && /\blast[\s-]?minute\b/i.test(discoverTitle)) {
		issues.push("Discover-/SEO-titlen må kun bruge last minute ved afrejse inden for 30 dage.")
	}
	if (!timing.isWeekendStay && /\bweekend(?:ophold|tur)?\b/i.test(discoverTitle)) {
		issues.push("Discover-/SEO-titlen må kun bruge weekend om et dokumenteret weekendophold.")
	}
	if (!timing.isNextWeekend && /\bnæste weekend\b/i.test(discoverTitle)) {
		issues.push("Discover-/SEO-titlen må kun bruge næste weekend, når datoerne passer.")
	}
	return issues
}

export function getDiscoverImageBlockingIssues(dimensions: ImageDimensions | null) {
	if (!dimensions) return ["Billedets dimensioner er endnu ikke kontrolleret."]
	const issues: string[] = []
	if (dimensions.width < 1200) issues.push("Billedet skal være mindst 1.200 px bredt.")
	if (dimensions.width * dimensions.height <= 300000) issues.push("Billedet skal indeholde mere end 300.000 pixels.")
	return issues
}

function isGenericTravelImageAlt(value: string, data: Record<string, string>) {
	const destination = data.destination || ""
	const country = data.land || ""
	const genericValues = [
		destination,
		`${destination}${country ? ` i ${country}` : ""}`,
		`Rejse til ${destination}`,
		`Rejse til ${destination}${country ? ` i ${country}` : ""}`,
		`Billede af ${destination}`,
		`Billede fra ${destination}`,
		`Foto af ${destination}`,
		`Foto fra ${destination}`,
	].map(normalizeHeadlineComparison)
	return value.trim().length < 12 || genericValues.includes(normalizeHeadlineComparison(value))
}

function getTravelDateDifference(startValue?: string, endValue?: string) {
	const start = new Date(`${startValue || ""}T12:00:00Z`)
	const end = new Date(`${endValue || ""}T12:00:00Z`)
	if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
	return Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000))
}

export function getTravelOfferApprovalIssues(data: Record<string, string>) {
	const issues: string[] = []
	const prices = PRICE_FIELDS.map((field) => parsePrice(data[field]))
	if (prices.some((price) => price === null || price <= 0)) {
		issues.push("Alle fire priser skal være større end 0 kr.")
	}

	const [flightPrice, hotelPrice, totalPrice, perPersonPrice] = prices
	if (flightPrice !== null && hotelPrice !== null && totalPrice !== null) {
		const expectedTotal = Math.round(flightPrice + hotelPrice)
		if (Math.abs(totalPrice - expectedTotal) > 2) {
			issues.push("Samlet pris skal svare til flypris plus laveste hotelpris.")
		}
	}
	if (totalPrice !== null && perPersonPrice !== null) {
		const expectedPerPerson = Math.round(totalPrice / 2)
		if (Math.abs(perPersonPrice - expectedPerPerson) > 2) {
			issues.push("Pris pr. person skal svare til halvdelen af den samlede pris.")
		}
	}

	if (!data.destination?.trim()) issues.push("Destination mangler.")
	if (!data.hotelnavn?.trim()) issues.push("Hotelnavn mangler.")
	const dateDifference = getTravelDateDifference(data.afrejsedato, data.hjemrejsedato)
	if (dateDifference === null || dateDifference <= 0) {
		issues.push("Afrejse- og hjemrejsedato skal være gyldige.")
	} else if (Number(data.antal_naetter || 0) !== dateDifference) {
		issues.push("Antal nætter stemmer ikke med afrejse- og hjemrejsedato.")
	}
	if (!LINK_FIELDS.every((field) => Boolean(extractHttpUrl(data[field])))) {
		issues.push("Både Momondo- og Trivago-link skal være gyldige.")
	}
	return issues
}

export function buildLocalHeadlineSuggestions(data: Record<string, string>) {
	const destination = data.destination || "destinationen"
	const nights = Number(data.antal_naetter || 0)
	const duration = nights > 0 ? `${nights} nætter` : "Rejse"
	const price = formatPrice(data.pris_pr_person_dkk)

	return {
		articleTitle: `Rejsetilbud til ${destination}: ${duration.toLocaleLowerCase("da-DK")} fra ${price} pr. person`,
		discoverTitle: "",
	}
}

function getControlVariant(value?: string) {
	const normalized = String(value || "").toLocaleLowerCase("da-DK")
	if (normalized.includes("bekræftet") && !normalized.includes("ikke")) return "success"
	if (normalized.includes("estimat")) return "warning"
	return "secondary"
}

function OfferSummary({ offer }: { offer: ImportedTravelOffer }) {
	const data = offer.data
	return (
		<>
			<div className="d-flex justify-content-between align-items-start gap-3 mb-3">
				<div>
					<div className="text-muted small mb-1">{data.land || "Rejsetilbud"}</div>
					<h5 className="mb-1">{data.destination || "Ukendt destination"}</h5>
					<div className="small text-muted">
						{formatTravelDate(data.afrejsedato)} – {formatTravelDate(data.hjemrejsedato)}
						{data.antal_naetter ? ` · ${data.antal_naetter} nætter` : ""}
					</div>
				</div>
				{data.tilbudstype ? <Badge bg="dark">{data.tilbudstype}</Badge> : null}
			</div>

			<div className="p-3 rounded bg-light mb-3">
				<div className="small text-muted">Pris pr. person</div>
				<div className="fs-4 fw-bold">{formatPrice(data.pris_pr_person_dkk)}</div>
				<div className="small text-muted">Samlet for 2: {formatPrice(data.samlet_pris_dkk)}</div>
				<div className="border-top mt-3 pt-2 d-flex flex-column gap-1 small">
					<div className="d-flex justify-content-between gap-3">
						<span className="text-muted">Fly for 2 voksne</span>
						<span className="fw-semibold">{formatPrice(data.flypris_2_voksne_dkk)}</span>
					</div>
					<div className="d-flex justify-content-between gap-3">
						<span className="text-muted">Hotel</span>
						<span className="fw-semibold">{formatPrice(data.hotelpris_min_dkk)}</span>
					</div>
				</div>
			</div>

			<Row className="g-3 small">
				<Col sm={6}>
					<div className="text-muted">Fly</div>
					<div className="fw-semibold">{data.flyselskab || "-"}</div>
					<div>{data.flyrute || [data.afrejselufthavn, data.destinationslufthavn].filter(Boolean).join(" → ") || "-"}</div>
					<div className="text-muted">{data.antal_stop === "0" ? "Direkte" : data.antal_stop ? `${data.antal_stop} stop` : ""}</div>
				</Col>
				<Col sm={6}>
					<div className="text-muted">Hotel</div>
					<div className="fw-semibold">{data.hotelnavn || "-"}</div>
					<div>{[data.hotelstjerner ? `${data.hotelstjerner} stjerner` : "", data.hotelrating ? `Rating ${data.hotelrating}` : ""].filter(Boolean).join(" · ")}</div>
					<div className="text-muted">{data.hotelomraade || ""}</div>
				</Col>
			</Row>

			<div className="d-flex gap-2 flex-wrap mt-3">
				{data.kontrolniveau ? <Badge bg={getControlVariant(data.kontrolniveau)} text={getControlVariant(data.kontrolniveau) === "warning" ? "dark" : undefined}>{data.kontrolniveau}</Badge> : null}
				{data.ledighedsstatus ? <Badge bg="light" text="dark" className="border">{data.ledighedsstatus}</Badge> : null}
			</div>
		</>
	)
}

export default function TravelArticleWorkflow({
	doFetch,
	onArticleCreated,
}: {
	doFetch: TravelDoFetch
	onArticleCreated?: (article: TravelCreatedArticle) => void
}) {
	const [fileName, setFileName] = useState("")
	const [headers, setHeaders] = useState<string[]>([])
	const [offers, setOffers] = useState<ImportedTravelOffer[]>([])
	const [selectedOffer, setSelectedOffer] = useState<ImportedTravelOffer | null>(null)
	const [showOfferModal, setShowOfferModal] = useState(false)
	const [error, setError] = useState("")
	const [offerApproved, setOfferApproved] = useState(false)
	const [imageDataUrl, setImageDataUrl] = useState("")
	const [imageFileName, setImageFileName] = useState("")
	const [imageDimensions, setImageDimensions] = useState<ImageDimensions | null>(null)
	const [imageAltText, setImageAltText] = useState("")
	const [articleTitle, setArticleTitle] = useState("")
	const [discoverTitle, setDiscoverTitle] = useState("")
	const [isGeneratingHeadlines, setIsGeneratingHeadlines] = useState(false)
	const [headlineSource, setHeadlineSource] = useState<"" | "ai" | "fallback">("")
	const [headlineError, setHeadlineError] = useState("")
	const [isCreatingArticle, setIsCreatingArticle] = useState(false)
	const [articleCreationError, setArticleCreationError] = useState("")
	const [createdArticle, setCreatedArticle] = useState<TravelCreatedArticle | null>(null)

	const additionalFields = useMemo(() => {
		return headers.filter((field) => !priorityFieldNames.has(field) && !HIDDEN_SECONDARY_FIELDS.has(field))
	}, [headers])

	const approvalIssues = useMemo(() => {
		if (!selectedOffer) return []
		return getTravelOfferApprovalIssues(selectedOffer.data)
	}, [selectedOffer])

	const canApproveOffer = Boolean(selectedOffer && !approvalIssues.length)
	const discoverIncludesDestination = Boolean(selectedOffer?.data.destination
		&& headlineIncludesValue(discoverTitle, selectedOffer.data.destination))
	const articleIncludesDestination = Boolean(selectedOffer?.data.destination
		&& headlineIncludesValue(articleTitle, selectedOffer.data.destination))
	const articleIncludesPrice = Boolean(selectedOffer && headlineContainsPrice(articleTitle, selectedOffer.data.pris_pr_person_dkk))
	const headlineSetupIssues = useMemo(() => {
		if (!selectedOffer) return []
		return getTravelHeadlineSetupIssues({
			articleTitle,
			discoverTitle,
			data: selectedOffer.data,
		})
	}, [articleTitle, discoverTitle, selectedOffer])
	const imageBlockingIssues = useMemo(() => getDiscoverImageBlockingIssues(imageDimensions), [imageDimensions])
	const imageQualityIssues = useMemo(() => {
		if (!imageDimensions) return []
		const issues: string[] = []
		const aspectRatio = imageDimensions.width / imageDimensions.height
		if (Math.abs(aspectRatio - (16 / 9)) > 0.15) {
			issues.push("Billedet afviger tydeligt fra 16:9. Kontrollér beskæringen i previewet.")
		}
		return issues
	}, [imageDimensions])
	const hasConcreteImageAlt = Boolean(selectedOffer
		&& imageAltText.trim()
		&& !isGenericTravelImageAlt(imageAltText, selectedOffer.data))
	const articleSetupReady = Boolean(imageDataUrl
		&& imageDimensions
		&& !imageBlockingIssues.length
		&& hasConcreteImageAlt
		&& !headlineSetupIssues.length)

	const resetArticleSetup = () => {
		setOfferApproved(false)
		setImageDataUrl("")
		setImageFileName("")
		setImageDimensions(null)
		setImageAltText("")
		setArticleTitle("")
		setDiscoverTitle("")
		setIsGeneratingHeadlines(false)
		setHeadlineSource("")
		setHeadlineError("")
		setIsCreatingArticle(false)
		setArticleCreationError("")
		setCreatedArticle(null)
	}

	const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) return

		const hasTsvExtension = file.name.toLocaleLowerCase("da-DK").endsWith(".tsv")
		if (!hasTsvExtension) {
			setError("Vælg en TSV-fil med filendelsen .tsv.")
			event.target.value = ""
			return
		}

		if (file.size > 5 * 1024 * 1024) {
			setError("TSV-filen er for stor. Maksimal størrelse er 5 MB.")
			event.target.value = ""
			return
		}

		try {
			const parsed = parseTravelOfferTsv(await readFileAsText(file))
			setFileName(file.name)
			setHeaders(parsed.headers)
			setOffers(parsed.offers)
			setSelectedOffer(null)
			resetArticleSetup()
			setError("")
			setShowOfferModal(true)
		} catch (fileError) {
			setFileName("")
			setHeaders([])
			setOffers([])
			setSelectedOffer(null)
			resetArticleSetup()
			setError(fileError instanceof Error ? fileError.message : "TSV-filen kunne ikke læses.")
			event.target.value = ""
		}
	}

	const chooseOffer = (offer: ImportedTravelOffer) => {
		if (selectedOffer?.id !== offer.id) {
			resetArticleSetup()
			setSelectedOffer({
				...offer,
				data: { ...offer.data },
			})
		}
		setShowOfferModal(false)
	}

	const generateHeadlineSuggestions = async (offerData = selectedOffer?.data) => {
		if (!offerData) return
		const fallback = buildLocalHeadlineSuggestions(offerData)
		setArticleTitle(fallback.articleTitle)
		setIsGeneratingHeadlines(true)
		setHeadlineSource("fallback")
		setHeadlineError("")

		try {
			const response = await doFetch("/eb-pristjek/articles/travel/headlines", {
				offer: offerData,
				variationSeed: `${Date.now()}-${Math.random()}`,
			})
			const data = await response.json() as TravelHeadlineResponse
			if (response.status >= 400) {
				throw new Error(data.error || "AI-forslaget kunne ikke oprettes.")
			}
			if (!String(data.discoverTitle || "").trim()) {
				throw new Error("AI returnerede ikke en Discover-overskrift.")
			}
			setArticleTitle(String(data.articleTitle || fallback.articleTitle))
			setDiscoverTitle(String(data.discoverTitle))
			setHeadlineSource("ai")
		} catch (headlineRequestError) {
			setHeadlineError(headlineRequestError instanceof Error
				? `${headlineRequestError.message} Den almindelige overskrift er indsat, men Discover-overskriften kræver et nyt AI-forsøg.`
				: "AI-forslaget kunne ikke oprettes. Den almindelige overskrift er indsat, men Discover-overskriften kræver et nyt AI-forsøg.")
		} finally {
			setIsGeneratingHeadlines(false)
		}
	}

	const approveOffer = () => {
		if (!selectedOffer || !canApproveOffer) return
		const verifiedOffer: ImportedTravelOffer = {
			...selectedOffer,
			data: {
				...selectedOffer.data,
				kontroltidspunkt: new Date().toISOString(),
				kontrolniveau: "Manuelt verificeret",
				ledighedsstatus: "Kontrolleret ved godkendelse",
			},
		}
		setSelectedOffer(verifiedOffer)
		setImageAltText("")
		setOfferApproved(true)
		void generateHeadlineSuggestions(verifiedOffer.data)
	}

	const handleArticleImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) {
			setImageDataUrl("")
			setImageFileName("")
			setImageDimensions(null)
			return
		}
		if (!SUPPORTED_ARTICLE_IMAGE_TYPES.has(String(file.type || "").toLocaleLowerCase("en-US"))) {
			setHeadlineError("Vælg et JPEG-, PNG-, GIF- eller WebP-billede.")
			event.target.value = ""
			return
		}
		if (file.size > 8 * 1024 * 1024) {
			setHeadlineError("Billedfilen er for stor. Maksimal størrelse er 8 MB.")
			event.target.value = ""
			return
		}

		try {
			const dataUrl = await readFileAsDataUrl(file)
			setImageDataUrl(dataUrl)
			setImageFileName(file.name)
			setImageDimensions(null)
			const image = new Image()
			image.onload = () => setImageDimensions({
				width: image.naturalWidth || image.width,
				height: image.naturalHeight || image.height,
			})
			image.onerror = () => {
				setImageDimensions(null)
				setHeadlineError("Billedet kunne ikke indlæses. Vælg en anden billedfil.")
			}
			image.src = dataUrl
			setHeadlineError("")
		} catch (imageError) {
			setImageDataUrl("")
			setImageFileName("")
			setImageDimensions(null)
			setHeadlineError(imageError instanceof Error ? imageError.message : "Billedfilen kunne ikke læses.")
		}
	}

	const createArticle = async () => {
		if (!selectedOffer || !offerApproved || !articleSetupReady || isCreatingArticle || createdArticle) return
		setIsCreatingArticle(true)
		setArticleCreationError("")

		try {
			const response = await doFetch("/eb-pristjek/articles/drafts", {
				articleType: "travel-article",
				title: articleTitle.trim(),
				discoverTitle: discoverTitle.trim(),
				featuredImageUrl: imageDataUrl,
				featuredImageAlt: imageAltText.trim(),
				featuredImageWidth: imageDimensions?.width || 0,
				featuredImageHeight: imageDimensions?.height || 0,
				travelOffer: selectedOffer.data,
			})
			const data = await response.json() as TravelArticleDraftResponse
			if (response.status >= 400) {
				throw new Error(data.error || "Rejseartiklen kunne ikke oprettes.")
			}

			const articleId = Number(data.articleId || data.createdPost?.id || 0)
			if (!articleId) throw new Error("API'et returnerede ikke et article id.")
			const created = {
				articleId,
				previewLink: String(data.previewLink || `${WP_SITE}/?p=${articleId}&preview=true&no_redirect=1`),
				editLink: String(data.editLink || `${WP_SITE}/wp-admin/post.php?post=${articleId}&action=edit`),
				title: String(data.draft?.title || articleTitle.trim()),
				topic: String(data.topic || data.draft?.topic || `rejse til ${selectedOffer.data.destination}`),
				articleTypeName: String(data.articleTypeName || "Rejseartikel"),
			}
			setCreatedArticle(created)
			onArticleCreated?.(created)
		} catch (createError) {
			setArticleCreationError(createError instanceof Error ? createError.message : "Rejseartiklen kunne ikke oprettes.")
		} finally {
			setIsCreatingArticle(false)
		}
	}

	const updateField = (field: string, value: string) => {
		setSelectedOffer((current) => current
			? {
				...current,
				data: {
					...current.data,
					[field]: value,
				},
			}
			: current)
	}

	const recalculatePrices = () => {
		if (!selectedOffer) return
		const flightPrice = parsePrice(selectedOffer.data.flypris_2_voksne_dkk)
		const hotelPrice = parsePrice(selectedOffer.data.hotelpris_min_dkk)
		if (flightPrice === null || hotelPrice === null) return
		const total = Math.round(flightPrice + hotelPrice)
		setSelectedOffer((current) => current
			? {
				...current,
				data: {
					...current.data,
					samlet_pris_dkk: String(total),
					pris_pr_person_dkk: String(Math.round(total / 2)),
				},
			}
			: current)
	}

	const renderTextField = (field: string, options: { multiline?: boolean; type?: string } = {}) => {
		if (!selectedOffer) return null
		const value = selectedOffer.data[field] || ""
		const multiline = options.multiline || value.length > 120
		return (
			<Form.Group controlId={`travel-${field}`}>
				<Form.Label className="small fw-semibold mb-1">{getFieldLabel(field)}</Form.Label>
				{multiline ? (
					<Form.Control
						as="textarea"
						rows={3}
						value={value}
						onChange={(event) => updateField(field, event.target.value)}
					/>
				) : (
					<Form.Control
						type={options.type || "text"}
						min={options.type === "number" ? 0 : undefined}
						step={options.type === "number" ? 1 : undefined}
						value={value}
						onChange={(event) => updateField(field, event.target.value)}
					/>
				)}
			</Form.Group>
		)
	}

	const renderLinkField = (field: string) => {
		if (!selectedOffer) return null
		const value = selectedOffer.data[field] || ""
		const url = extractHttpUrl(value)
		return (
			<Form.Group controlId={`travel-${field}`}>
				<Form.Label className="small fw-semibold mb-1">{getFieldLabel(field)}</Form.Label>
				<div className="d-flex gap-2 align-items-start">
					<Form.Control
						className="flex-grow-1"
						type="url"
						value={value}
						placeholder="https://"
						onChange={(event) => updateField(field, event.target.value)}
					/>
					{url ? (
						<a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary text-nowrap">
							Åbn link ↗
						</a>
					) : (
						<Button variant="outline-secondary" disabled className="text-nowrap">Intet link</Button>
					)}
				</div>
			</Form.Group>
		)
	}

	return (
		<div>
			<Alert variant="info">
				<strong>Rejseartikel-workflow</strong>
				<div className="small mt-1">Upload tilbudsfilen, kontrollér ét tilbud, godkend det og klargør derefter billede og overskrifter.</div>
			</Alert>

			<Form.Group controlId="travelOffersFile" className="mb-3">
				<Form.Label>Rejsetilbud (TSV)</Form.Label>
				<Form.Control type="file" accept=".tsv,text/tab-separated-values,text/plain" onChange={handleFileChange} />
				<Form.Text className="text-muted">
					{fileName ? `${fileName} · ${offers.length} tilbud indlæst` : "Vælg en TSV-fil med samme kolonner som rejsetilbud-formatet."}
				</Form.Text>
			</Form.Group>

			{error ? <Alert variant="danger">{error}</Alert> : null}

			{offers.length ? (
				<div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
					<div className="small text-muted">{offers.length} tilbud er klar til gennemgang.</div>
					<Button variant="outline-primary" onClick={() => setShowOfferModal(true)}>
						{selectedOffer ? "Vælg et andet tilbud" : "Vælg tilbud"}
					</Button>
				</div>
			) : null}

			{selectedOffer ? (
				<div className="d-flex flex-column gap-3">
					<Card className="border-primary">
						<Card.Body>
							<div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
								<div>
									<div className="small text-primary fw-semibold mb-1">Valgt tilbud</div>
									<h5 className="mb-1">{selectedOffer.data.destination}, {selectedOffer.data.land}</h5>
									<div className="text-muted small">
										{formatTravelDate(selectedOffer.data.afrejsedato)} – {formatTravelDate(selectedOffer.data.hjemrejsedato)}
										{" · "}{selectedOffer.data.hotelnavn}
									</div>
								</div>
								<div className="text-end">
									<div className="small text-muted">Pris pr. person</div>
									<div className="fs-5 fw-bold">{formatPrice(selectedOffer.data.pris_pr_person_dkk)}</div>
								</div>
							</div>
						</Card.Body>
					</Card>

					{!offerApproved ? (
						<>
					<Card>
						<Card.Body>
							<div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
									<div>
										<h6 className="mb-1">1. Kontrollér priser</h6>
										<div className="small text-muted">Ret priserne efter kontrol. Samlet pris kan genberegnes som fly + laveste hotelpris.</div>
								</div>
								<Button
									size="sm"
									variant="outline-secondary"
									onClick={recalculatePrices}
									disabled={parsePrice(selectedOffer.data.flypris_2_voksne_dkk) === null || parsePrice(selectedOffer.data.hotelpris_min_dkk) === null}
								>
									Genberegn samlet pris
								</Button>
							</div>
							<Row className="g-3">
								{PRICE_FIELDS.map((field) => (
									<Col md={6} key={field}>{renderTextField(field, { type: "number" })}</Col>
								))}
							</Row>
						</Card.Body>
					</Card>

					<Card>
						<Card.Body>
							<h6 className="mb-1">2. Validér prislinks</h6>
							<div className="small text-muted mb-3">Links åbner i en ny fane, så de aktuelle priser kan sammenholdes med tilbuddet.</div>
							<div className="d-flex flex-column gap-3">
								{LINK_FIELDS.map((field) => <div key={field}>{renderLinkField(field)}</div>)}
							</div>
						</Card.Body>
					</Card>

					<details className="border rounded bg-white">
						<summary className="p-3" style={{ cursor: "pointer" }}>
							<span className="fw-semibold">3. Ret tilbuddets oplysninger</span>
							<span className="d-block small text-muted mt-1">Åbn for at redigere rejse-, fly- og hoteloplysninger.</span>
						</summary>
						<div className="p-3 pt-0">
							<Row className="g-3">
								{PRIMARY_DETAIL_FIELDS.map((field) => (
									<Col md={field === "artikeloverskrift_forslag" || field === "bagage_status" ? 12 : 6} key={field}>
										{renderTextField(field, {
											type: field.endsWith("dato") ? "date" : field === "antal_naetter" || field === "antal_stop" || field === "hotelstjerner" || field === "hotelrating_antal_anmeldelser" ? "number" : "text",
											multiline: field === "bagage_status",
										})}
									</Col>
								))}
							</Row>
						</div>
					</details>

					<details className="border rounded bg-white">
						<summary className="p-3 fw-semibold" style={{ cursor: "pointer" }}>
							Redigér øvrige importerede data ({additionalFields.length})
						</summary>
						<div className="p-3 pt-0">
							<Row className="g-3">
								{additionalFields.map((field) => (
									<Col md={6} key={field}>
										{renderTextField(field)}
									</Col>
								))}
							</Row>
						</div>
					</details>

					<Card className="border-success">
						<Card.Body className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
							<div>
								<h6 className="mb-1">Tilbuddet er verificeret</h6>
								<div className="small text-muted">
									Godkend tilbuddet, når priserne er rettet, og Momondo- og Trivago-linkene er kontrolleret.
								</div>
								{!canApproveOffer ? (
									<ul className="small text-danger mt-2 mb-0 ps-3">
										{approvalIssues.map((issue) => <li key={issue}>{issue}</li>)}
									</ul>
								) : null}
							</div>
							<Button variant="success" onClick={approveOffer} disabled={!canApproveOffer}>
								Godkend verificeret tilbud
							</Button>
						</Card.Body>
					</Card>
						</>
					) : (
						<>
							<Alert variant="success" className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-0">
								<div>
									<strong>Tilbuddet er godkendt</strong>
									<div className="small mt-1">Priser og links er låst som grundlag for artikelopsætningen.</div>
								</div>
								<Button
									variant="outline-success"
									size="sm"
									onClick={() => {
										setOfferApproved(false)
										setHeadlineError("")
									}}
								>
									Ret tilbuddet igen
								</Button>
							</Alert>

							<Card>
								<Card.Body>
									<div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
										<div>
											<h6 className="mb-1">4. Klargør artikelopsætning</h6>
											<div className="small text-muted">Upload artikelbilledet, og tilpas de to overskrifter før næste trin.</div>
										</div>
										<Button
											size="sm"
											variant="outline-primary"
											onClick={() => void generateHeadlineSuggestions()}
											disabled={isGeneratingHeadlines || isCreatingArticle || Boolean(createdArticle)}
										>
											{isGeneratingHeadlines ? (
												<>
													<Spinner animation="border" size="sm" className="me-2" />
													Skriver forslag...
												</>
											) : "Foreslå nye overskrifter med AI"}
										</Button>
									</div>

									<Form.Group controlId="travelArticleImage" className="mb-3">
										<Form.Label className="fw-semibold">Artikelbillede</Form.Label>
										<Form.Control type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleArticleImageChange} disabled={isCreatingArticle || Boolean(createdArticle)} />
										<Form.Text className="text-muted">
											{imageFileName
												? `Valgt billede: ${imageFileName}${imageDimensions ? ` · ${imageDimensions.width} × ${imageDimensions.height} px` : ""}`
												: "Vælg et individuelt, relevant billede uden meget tekst. Det skal være mindst 1.200 px bredt og mere end 300.000 pixels, gerne omkring 16:9."}
										</Form.Text>
									</Form.Group>

									{imageDataUrl ? (
										<>
											<div className="border rounded overflow-hidden mb-2 bg-light text-center">
												<img
													src={imageDataUrl}
													alt={`Forhåndsvisning af artikelbillede til ${selectedOffer.data.destination}`}
													style={{ display: "block", width: "100%", aspectRatio: "16 / 9", objectFit: "cover" }}
												/>
											</div>
											<div className="small text-muted mb-2">16:9-preview af den beskæring, som kan blive brugt i Discover.</div>
											{imageDimensions && imageBlockingIssues.length ? (
												<Alert variant="danger" className="py-2 small">
													<strong>Billedet kan ikke bruges til denne rejseartikel.</strong>{" "}
													{imageBlockingIssues.join(" ")}
												</Alert>
											) : imageDimensions && imageQualityIssues.length ? (
												<Alert variant="info" className="py-2 small">
													<strong>Billedet er godkendt og kan bruges.</strong>{" "}
													{imageQualityIssues.join(" ")} 16:9 er kun en anbefaling og blokerer ikke oprettelsen.
												</Alert>
											) : imageDimensions ? (
												<div className="small text-success mb-3">Billedets størrelse og format er velegnet til Discover.</div>
											) : null}
										</>
									) : null}

									<Form.Group controlId="travelArticleImageAlt" className="mb-3">
										<Form.Label className="fw-semibold">Billedbeskrivelse (alt-tekst)</Form.Label>
										<Form.Control
											type="text"
											maxLength={140}
											value={imageAltText}
											onChange={(event) => setImageAltText(event.target.value)}
											disabled={isCreatingArticle || Boolean(createdArticle)}
											placeholder={`Beskriv kort, hvad billedet viser i ${selectedOffer.data.destination}`}
										/>
										<Form.Text className="text-muted">
											Beskriv det konkrete motiv, ikke blot destinationen. Feltet er obligatorisk af hensyn til tilgængelighed og billedsøgning.
										</Form.Text>
										{imageAltText.trim() && !hasConcreteImageAlt ? (
											<div className="small text-danger mt-1">Billedbeskrivelsen skal fortælle, hvad motivet viser.</div>
										) : null}
									</Form.Group>

									<Form.Group controlId="travelArticleTitle" className="mb-3">
										<div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
											<Form.Label className="fw-semibold">Overskrift i artiklen</Form.Label>
											{!isGeneratingHeadlines && headlineSource ? (
												<Badge bg={headlineSource === "ai" ? "primary" : "secondary"}>
													{headlineSource === "ai" ? "AI-forslag" : "Standardforslag"}
												</Badge>
											) : null}
										</div>
										<Form.Control
											type="text"
											maxLength={110}
											value={articleTitle}
											onChange={(event) => setArticleTitle(event.target.value)}
											disabled={isCreatingArticle || Boolean(createdArticle)}
											placeholder="Den synlige H1-overskrift i artiklen"
										/>
										<Form.Text className="text-muted">
											Denne overskrift vises som artiklens almindelige titel og skal altid indeholde den verificerede pris pr. person. {articleTitle.length}/110 tegn.
										</Form.Text>
									</Form.Group>

									<Form.Group controlId="travelDiscoverTitle">
										<Form.Label className="fw-semibold">Discover-/SEO-titel</Form.Label>
										<Form.Control
											type="text"
											maxLength={80}
											value={discoverTitle}
											onChange={(event) => setDiscoverTitle(event.target.value)}
											disabled={isCreatingArticle || Boolean(createdArticle)}
											placeholder="Den korte titel til feeds og SEO"
										/>
										<Form.Text className="text-muted">
											Dette er Yoast-titlen, ikke et særligt Google Discover-felt. Den skal være præcis, højst 80 tegn og må gerne matche H1. En verificeret pris er tilladt. {discoverTitle.length}/80 tegn.
										</Form.Text>
									</Form.Group>

									{headlineError ? <Alert variant="warning" className="mt-3 mb-0">{headlineError}</Alert> : null}
									{(articleTitle || discoverTitle) && headlineSetupIssues.length ? (
										<Alert variant="warning" className="mt-3 mb-0 py-2">
											<ul className="mb-0 ps-3">
												{headlineSetupIssues.map((issue) => <li key={issue}>{issue}</li>)}
											</ul>
										</Alert>
									) : null}
									{imageDataUrl || articleTitle || discoverTitle ? (
										<div className="border rounded p-3 mt-3">
											<div className="fw-semibold mb-2">SEO- og Discover-tjek</div>
											<div className="d-flex gap-2 flex-wrap">
												<Badge bg={imageDataUrl && imageDimensions && !imageBlockingIssues.length && hasConcreteImageAlt ? "success" : "warning"} text={imageDataUrl && imageDimensions && !imageBlockingIssues.length && hasConcreteImageAlt ? undefined : "dark"}>Discover-billede og alt-tekst</Badge>
												<Badge bg={articleIncludesDestination ? "success" : "warning"} text={articleIncludesDestination ? undefined : "dark"}>Destination i artikeloverskrift</Badge>
												<Badge bg={articleIncludesPrice ? "success" : "warning"} text={articleIncludesPrice ? undefined : "dark"}>Pris i artikeloverskrift</Badge>
												<Badge bg={discoverIncludesDestination ? "success" : "warning"} text={discoverIncludesDestination ? undefined : "dark"}>Destination i Discover-/SEO-titel</Badge>
											</div>
										</div>
									) : null}
									{articleSetupReady ? (
										<Alert variant="success" className="mt-3 mb-0">
											<strong>Artikelopsætningen er klar.</strong> Billede, alt-tekst og begge overskrifter opfylder kravene.
										</Alert>
									) : (
										<div className="small text-muted mt-3">Upload et godkendt billede, skriv en konkret billedbeskrivelse, og ret de viste titelproblemer.</div>
									)}

									{articleCreationError ? <Alert variant="danger" className="mt-3 mb-0">{articleCreationError}</Alert> : null}

									{createdArticle ? (
										<Alert variant="success" className="mt-3 mb-0">
											<div className="fw-semibold mb-2">Rejseartiklen er oprettet som WordPress-kladde med ID {createdArticle.articleId}.</div>
											<div className="small mb-2">Publicér samme kalenderdag som tilbuddet blev kontrolleret. Kontrollér pris, ledighed og links igen ved senere publicering.</div>
											<div className="d-flex gap-2 flex-wrap">
												<Button as="a" href={createdArticle.previewLink} target="_blank" rel="noopener noreferrer" size="sm" variant="outline-success">
													Åbn preview
												</Button>
												<Button as="a" href={createdArticle.editLink} target="_blank" rel="noopener noreferrer" size="sm" variant="outline-dark">
													Redigér i WordPress
												</Button>
											</div>
										</Alert>
									) : (
										<div className="d-grid mt-3">
											<Button
												variant="success"
												size="lg"
												disabled={!articleSetupReady || isCreatingArticle}
												onClick={() => void createArticle()}
											>
												{isCreatingArticle ? (
													<>
														<Spinner animation="border" size="sm" className="me-2" />
														Opretter rejseartikel...
													</>
												) : "Opret rejseartikel som kladde"}
											</Button>
										</div>
									)}
								</Card.Body>
							</Card>
						</>
					)}
					</div>
			) : null}

			<Modal
				show={showOfferModal}
				onHide={() => setShowOfferModal(false)}
				size="xl"
				centered
				scrollable
				aria-labelledby="travel-offer-modal-title"
			>
				<Modal.Header closeButton>
					<Modal.Title id="travel-offer-modal-title">Vælg rejsetilbud</Modal.Title>
				</Modal.Header>
				<Modal.Body className="bg-light">
					<div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
						<div>
							<div className="fw-semibold">{fileName}</div>
							<div className="small text-muted">Sammenlign de vigtigste oplysninger og vælg det tilbud, artiklen skal tage udgangspunkt i.</div>
						</div>
						<Badge bg="dark">{offers.length} tilbud</Badge>
					</div>
					<Row xs={1} lg={2} className="g-3">
						{offers.map((offer) => (
							<Col key={offer.id}>
								<Card className={`h-100 shadow-sm ${selectedOffer?.id === offer.id ? "border-primary" : "border-0"}`}>
									<Card.Body className="d-flex flex-column">
										<OfferSummary offer={selectedOffer?.id === offer.id ? selectedOffer : offer} />
										<div className="mt-auto pt-3 d-grid">
											<Button
												variant={selectedOffer?.id === offer.id ? "primary" : "outline-primary"}
												onClick={() => chooseOffer(offer)}
											>
												{selectedOffer?.id === offer.id ? "Fortsæt med dette tilbud" : "Vælg tilbud"}
											</Button>
										</div>
									</Card.Body>
								</Card>
							</Col>
						))}
					</Row>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="outline-secondary" onClick={() => setShowOfferModal(false)}>Luk</Button>
				</Modal.Footer>
			</Modal>
		</div>
	)
}
